import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Bike, Shield, Clock, MapPin, Phone, Mail } from "lucide-react";

const AboutFleetyGoPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="about-fleetygo">
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">About FleetyGo</h1>
      </header>

      <main className="flex-1 px-4 py-6">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="text-[#3FA9F5]" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">FleetyGo</h1>
          <p className="text-gray-600">Send Anything, Anywhere</p>
        </div>

        {/* About Section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">About Us</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            FleetyGo is a hyperlocal delivery service that enables you to send packages up to 20 kg 
            anywhere in the city. Whether it's documents, food, medicine, or gifts – we deliver it 
            fast and safe on two-wheelers.
          </p>
        </section>

        {/* Features */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Why Choose FleetyGo?</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock size={20} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Fast Delivery</h3>
                <p className="text-sm text-gray-500">Same-day delivery across the city</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Live Tracking</h3>
                <p className="text-sm text-gray-500">Track your package in real-time on the map</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Secure & Safe</h3>
                <p className="text-sm text-gray-500">PIN-based pickup & delivery verification</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bike size={20} className="text-orange-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Eco-Friendly</h3>
                <p className="text-sm text-gray-500">Two-wheeler deliveries reduce carbon footprint</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Transparent Pricing</h2>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Per km rate</span>
                <span className="font-medium">₹10</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Minimum charge (0-3 km)</span>
                <span className="font-medium">₹30</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Platform fee</span>
                <span className="font-medium">₹20</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">GST (18%)</span>
                <span className="font-medium">₹3.60</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-600">Wait time (after 10 mins)</span>
                <span className="font-medium">₹1/min</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">* First 10 minutes of wait time is free</p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Contact Us</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail size={18} className="text-[#3FA9F5]" />
              <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5]">support@fleety.co.in</a>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={18} className="text-[#3FA9F5]" />
              <a href="tel:+919845331817" className="text-[#3FA9F5]">+91-98453 31817</a>
            </div>
            <div className="flex items-center gap-3">
              <MapPin size={18} className="text-[#3FA9F5]" />
              <span className="text-gray-600">Bengaluru, India</span>
            </div>
          </div>
        </section>

        {/* Legal Links */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Legal</h2>
          <div className="space-y-2">
            <a href="/fleetygo/terms" className="block text-[#3FA9F5] text-sm hover:underline">
              Terms & Conditions →
            </a>
            <a href="/fleetygo/privacy" className="block text-[#3FA9F5] text-sm hover:underline">
              Privacy Policy →
            </a>
            <a href="/fleetygo/faqs" className="block text-[#3FA9F5] text-sm hover:underline">
              FAQs →
            </a>
          </div>
        </section>

        <p className="text-center text-xs text-gray-400">© 2025 Fleety. All rights reserved.</p>
      </main>
    </div>
  );
};

export default AboutFleetyGoPage;
